import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Github, Send } from 'lucide-react';
import Card from '../components/ui/Card.tsx';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { Rocket } from 'lucide-react';
import StripePopup from '../components/ui/StripePopup.tsx';
import { useStripePopup } from '../hooks/useStripePopup.ts';

interface ContactProps {
  onNavigate: (page: string) => void;
}

const Contact: React.FC<ContactProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { popup, showPopup, hidePopup } = useStripePopup();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Envoi temporairement via le client mail de l'utilisateur (mailto:),
    // le temps de rebrancher un fournisseur SMTP.
    const subject = encodeURIComponent(`[4urcloud] Contact — ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}

${formData.message}`
    );
    window.location.href = `mailto:contact@4urcloud.com?subject=${subject}&body=${body}`;
    setIsSubmitted(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  
  const contactMethods = [
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: t('contact.methods.community.title'),
      description: t('contact.methods.community.description'),
      contact: t('contact.methods.community.contact'),
      action: 'https://join.slack.com/t/kexaio/shared_invite/zt-343gsrm6d-y9glG4khlH7kuecoerYCOQ',
    },
    {
      icon: <Github className="w-8 h-8" />,
      title: t('contact.methods.github.title'),
      description: t('contact.methods.github.description'),
      contact: t('contact.methods.github.contact'),
      action: 'https://github.com/4urcloud/kexa',
    },
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen">
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-2xl mx-auto"
            >
              <Card className="p-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {t('contact.success.title')}
                </h2>
                <p className="text-gray-600 mb-8">
                  {t('contact.success.message')}
                </p>
                <Button
                  variant="primary"
                  onClick={() => setIsSubmitted(false)}
                >
                  {t('contact.success.button')}
                </Button>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <SEO
        title="Contact Kexa Premium Sales & Support Team"
        description="Get expert help with Kexa Premium. Contact our sales team for custom pricing, deployment assistance, or technical support for your cloud security needs."
        keywords="kexa contact, cloud security support, enterprise sales, compliance monitoring support, technical assistance"
      />
      <section className="bg-white py-16 md:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('contact.title.prefix')}
            <GradientText variant="primary">{t('contact.title.highlight')}</GradientText>
            {t('contact.title.suffix')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Card>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('contact.form.title')}
              </h2>
              {/* Formulaire remplacé temporairement par l'adresse email directe */}
              <div className="text-center py-10">
                <p className="text-lg mb-4">Write to us directly:</p>
                <a
                  href="mailto:contact@4urcloud.eu"
                  className="text-2xl font-semibold text-primary hover:underline break-all"
                >
                  contact@4urcloud.eu
                </a>
              </div>
            </Card>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {contactMethods.map((method, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <Card hover className="h-full text-center flex flex-col">
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-xl flex items-center justify-center text-white mx-auto mb-4">
                  {method.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {method.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {method.description}
                </p>
                <Button
                  href={method.action}
                  variant="ghost"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  {method.contact}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      </section>
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.7 }}
        className="py-20 bg-gradient-hero relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 animate-slide-up">
            {t('common.buttons.readyToSecure')}
          </h2>
          <div className="w-16 h-px bg-white/40 mx-auto mb-8 animate-fade-in"></div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              onClick={() => onNavigate('plans')}
              variant="cta"
              size="lg"
              leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>}
            >
              {t('common.buttons.explorePlans')}
            </Button>
            <Button
              onClick={() => onNavigate('roadmap')}
              variant="cta-glass"
              size="lg"
              rightIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>}
            >
              {t('common.buttons.viewRoadmap')}
            </Button>
            <Button
              onClick={() => onNavigate('deploy')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Rocket size={20} />}
            >
              {t('common.buttons.startDeploying')}
            </Button>
          </div>
        </div>
      </motion.section>
      
      <StripePopup
        isOpen={popup.isOpen}
        onClose={hidePopup}
        message={popup.message}
        type={popup.type}
      />
    </div>
  );
};

export default Contact;